/**
 * Pagina storico movimenti di magazzino.
 *
 * Mostra tutti i movimenti dell'anno corrente con tipo, quantità colorata
 * e riferimenti esterni. La tabella è partizionata per anno lato backend.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormatters } from '@lib/formatters';

interface Movimento {
  id: string;
  created_at: string;
  tipo: string;
  articolo_id: string;
  articolo_nome: string | null;
  articolo_sku: string | null;
  quantita: number;
  riferimento_esterno: string | null;
  note: string | null;
}

/**
 * Pagina lista movimenti con quantità colorata e badge tipo.
 */
const MovimentiList: React.FC = () => {
  const fmt = useFormatters();
  const [movimenti, setMovimenti] = useState<Movimento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Anno corrente derivato dal sistema, non hardcoded (DEC-018). */
  const currentYear = new Date().getFullYear();

  const caricaMovimenti = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('year', String(currentYear));
      const response = await fetch(
        `/api/v1/magazzino/movimenti?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
          },
        },
      );
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      const data: Movimento[] = await response.json();
      setMovimenti(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [currentYear]);

  useEffect(() => {
    void caricaMovimenti();
  }, [caricaMovimenti]);

  const formatDateTime = (iso: string): string => fmt.dateTime(iso);

  return (
    <div className="space-y-6 p-6">
      <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
        Movimenti di magazzino — {currentYear}
      </h1>

      <Card>
        <CardHeader>
          <CardTitle>Storico movimenti</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading && (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Caricamento…
            </p>
          )}
          {error && (
            <p className="p-6 text-center text-sm text-destructive">{error}</p>
          )}
          {!loading && !error && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Articolo</TableHead>
                  <TableHead className="text-right">Quantità</TableHead>
                  <TableHead>Riferimento</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movimenti.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="text-center text-sm text-muted-foreground"
                    >
                      Nessun movimento registrato
                    </TableCell>
                  </TableRow>
                )}
                {movimenti.map((mov: Movimento) => (
                  <TableRow key={mov.id}>
                    <TableCell className="text-sm">
                      {formatDateTime(mov.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{mov.tipo}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {mov.articolo_sku && (
                        <span className="mr-2 font-mono text-muted-foreground">
                          {mov.articolo_sku}
                        </span>
                      )}
                      {mov.articolo_nome ?? mov.articolo_id.slice(0, 8)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      <span
                        className={
                          mov.quantita > 0
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-destructive'
                        }
                      >
                        {mov.quantita > 0 ? `+${mov.quantita}` : mov.quantita}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm">
                      {mov.riferimento_esterno ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {mov.note ?? '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MovimentiList;
